"use client"

import Link from "next/link"
import { useFormik, FormikProvider } from "formik"
import { Loader2 } from "lucide-react"
import { useAdminPartnersQuery, useAdminProgramSettingsQuery } from "@/hooks/queries/partners"
import { useUpdateAdminProgramSettingsMutation } from "@/hooks/mutations/partners"
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { FormInput, FormSwitch } from "@/components/form-inputs"
import { formatMicroUsd, parseUsdToMicroUsd } from "@/lib/billing/utils/format"
import { formatPercent } from "@/lib/partners/percent"
import type { AdminPartnerView } from "@/server-actions/admin-partners"
import { useTranslations } from "next-intl"




type ProgramSettingsForm = {
  programEnabled: boolean
  defaultCommissionPercent: string
  walletTransferEnabled: boolean
  cashPayoutEnabled: boolean
  cashPayoutFeePercent: string
  minPayout: string
}

export function ProgramSettingsTab() {


  const tUi = useTranslations("Partners.PartnersAdminProgramSettingsTab")

  const settings = useAdminProgramSettingsQuery()
  const partners = useAdminPartnersQuery()
  const mutation = useUpdateAdminProgramSettingsMutation()

  const formik = useFormik<ProgramSettingsForm>({
    enableReinitialize: true,
    initialValues: {
      programEnabled: settings.data?.program_enabled ?? true,
      defaultCommissionPercent: settings.data ? String(settings.data.default_commission_percent) : "",
      walletTransferEnabled: settings.data?.wallet_transfer_enabled ?? true,
      cashPayoutEnabled: settings.data?.cash_payout_enabled ?? false,
      cashPayoutFeePercent: settings.data ? String(settings.data.cash_payout_fee_percent) : "",
      minPayout: settings.data ? String(settings.data.min_payout_microusd / 1_000_000) : "",
    },
    validate,
    onSubmit: async values => {
      const minPayoutMicrousd = parseUsdToMicroUsd(values.minPayout.replace(",", "."))
      if (minPayoutMicrousd === null) return

      try {
        await mutation.mutateAsync({
          program_enabled: values.programEnabled,
          default_commission_percent: parsePercent(values.defaultCommissionPercent),
          wallet_transfer_enabled: values.walletTransferEnabled,
          cash_payout_enabled: values.cashPayoutEnabled,
          cash_payout_fee_percent: parsePercent(values.cashPayoutFeePercent),
          min_payout_microusd: minPayoutMicrousd,
        })
      } catch {
        // withQueryHandling already surfaced the error.
      }
    },
  })

  if (settings.isError) {
    return <Alert variant="destructive"><AlertDescription><span key={tUi("unableToLoadTheProgramSettingsPleaseRefresh")}>{tUi("unableToLoadTheProgramSettingsPleaseRefresh")}</span></AlertDescription></Alert>
  }

  if (settings.isPending) {
    return (
      <div className="flex min-h-32 items-center justify-center gap-2 text-sm text-muted-foreground">
        <Loader2 className="h-4 w-4 animate-spin" /> <span key={tUi("loadingSettings")}>{tUi("loadingSettings")}</span></div>
    )
  }

  const nextDefault = parsePercent(formik.values.defaultCommissionPercent)
  const clamped = Number.isFinite(nextDefault) ? partnersClampedBy(partners.data ?? [], nextDefault) : []

  return (
    <FormikProvider value={formik}>
      <form onSubmit={formik.handleSubmit} className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base"><span key={tUi("programDefaults")}>{tUi("programDefaults")}</span></CardTitle>
            <CardDescription>
              <span key={tUi("applyToEveryPartnerUnlessTenantManagementThe")}>{tUi("applyToEveryPartnerUnlessTenantManagementThe")}</span></CardDescription>
          </CardHeader>
          <CardContent className="max-w-lg space-y-4">
            <FormSwitch
              name="programEnabled"
              label={tUi("partnerProgram")}
              description={tUi("offPausesTheProgramForEveryTenantWithout")}
            />
            <FormInput
              name="defaultCommissionPercent"
              label={tUi("defaultCommission")}
              inputMode="decimal"
              description={tUi("usedWhenNeitherTheReferralLinkNorThe")}
            />

            {clamped.length > 0 && (
              <Alert variant="destructive">
                <AlertTitle><span key={tUi("clientBonusesWillBeClamped")}>{tUi("clientBonusesWillBeClamped")}</span></AlertTitle>
                <AlertDescription>
                  <p>
                    {<span key={tUi("thesePartnersFollowTheDefaultAndHaveLinks", { value0: String(formatPercent(nextDefault)) })}>{tUi("thesePartnersFollowTheDefaultAndHaveLinks", { value0: String(formatPercent(nextDefault)) })}</span>}
                  </p>
                  <ul className="mt-2 list-inside list-disc">
                    {clamped.map(partner => (
                      <li key={partner.partner_id}>
                        {<span key={tUi("value0UpToValue1Promised", { value0: String(partner.tenant_name), value1: String(formatPercent(partner.max_active_bonus_percent)) })}>{tUi("value0UpToValue1Promised", { value0: String(partner.tenant_name), value1: String(formatPercent(partner.max_active_bonus_percent)) })}</span>}
                      </li>
                    ))}
                  </ul>
                </AlertDescription>
              </Alert>
            )}
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base"><span key={tUi("withdrawalMethods")}>{tUi("withdrawalMethods")}</span></CardTitle>
            <CardDescription>
              <span key={tUi("howPartnersTakeMoneyOutTheseApplyTo")}>{tUi("howPartnersTakeMoneyOutTheseApplyTo")}</span></CardDescription>
          </CardHeader>
          <CardContent className="max-w-lg space-y-4">
            <FormSwitch
              name="walletTransferEnabled"
              label={tUi("transferToWallet")}
              description={tUi("partnersMoveTheirBalanceIntoTheirOwnWallet")}
            />
            <p className="text-xs text-muted-foreground">
              <span><span key={tUi("transferredCreditsAndClientBonusesExpireAfterThe")}>{tUi("transferredCreditsAndClientBonusesExpireAfterThe")}</span></span>{" "}
              <Link href="/?page=billing-admin&tab=settings" className="font-medium text-foreground underline underline-offset-4">
                <span key={tUi("billingAdminSettings")}>{tUi("billingAdminSettings")}</span></Link>
              <span>.</span>
            </p>
            <FormSwitch
              name="cashPayoutEnabled"
              label={tUi("cashPayout")}
              description={tUi("partnersRequestMoneyItLeavesTheirBalanceAt")}
            />
            <FormInput
              name="cashPayoutFeePercent"
              label={tUi("cashPayoutFee")}
              inputMode="decimal"
              description={tUi("withheldFromEachCashPayoutAt10A")}
            />
            <FormInput
              name="minPayout"
              label={tUi("minimumPayoutUSD")}
              inputMode="decimal"
              description={tUi("smallestAmountAPartnerMayRequestAsA")}
            />
          </CardContent>
        </Card>

        <div className="flex flex-wrap items-center gap-3">
          <Button type="submit" disabled={mutation.isPending}>
            {mutation.isPending && <Loader2 className="mr-1.5 h-4 w-4 animate-spin" />}
            <span key={tUi("saveSettings")}>{tUi("saveSettings")}</span></Button>
          <p className="text-xs text-muted-foreground">
            {<span key={tUi("currentMinimumValue0", { value0: String(formatMicroUsd(settings.data.min_payout_microusd)) })}>{tUi("currentMinimumValue0", { value0: String(formatMicroUsd(settings.data.min_payout_microusd)) })}</span>}
          </p>
        </div>
      </form>
    </FormikProvider>
  )
}

/**
 * Only partners without their own percent follow the default; a partner-level
 * override shields their links from this change.
 */
function partnersClampedBy(partners: AdminPartnerView[], percent: number): AdminPartnerView[] {
  return partners.filter(partner =>
    partner.commission_percent_override === null
    && partner.max_active_bonus_percent > percent)
}

function validate(values: ProgramSettingsForm) {
  const errors: Partial<Record<keyof ProgramSettingsForm, string>> = {}
  const percent = parsePercent(values.defaultCommissionPercent)
  const fee = parsePercent(values.cashPayoutFeePercent)
  const minPayout = parseUsdToMicroUsd(values.minPayout.replace(",", "."))

  if (!values.defaultCommissionPercent.trim() || !Number.isFinite(percent) || percent < 0 || percent > 100) {
    errors.defaultCommissionPercent = "Enter a percentage between 0 and 100."
  }
  // Each request stores its fee with two decimals.
  if (!values.cashPayoutFeePercent.trim() || !Number.isFinite(fee) || fee < 0 || fee >= 100
    || !/^\d+([.,]\d{1,2})?$/.test(values.cashPayoutFeePercent.trim())) {
    errors.cashPayoutFeePercent = "Enter a fee from 0 to 99.99, at most two decimals."
  }
  if (minPayout === null || minPayout < 0) {
    errors.minPayout = "Enter an amount in USD."
  }

  return errors
}

function parsePercent(value: string): number {
  return Number(value.replace(",", "."))
}
